/**
 * 入力スキーマの共通部品（文字列の種別・DB 参照・実装参照）と、欄が取る値の allowlist。
 *
 * 型ごとの入力スキーマは `contract/elements/` が持ち、ここの部品を使う。
 *
 * 文字列フィールドは用途で 3 種に分かれる。**人が読む本文**は `localizedText()` で全 locale
 * ぶんを一度に要求し、**ID として下流に流れる値**は `identifierString()`、**言語に依らない値**
 * （URL パターン・アイコン）は `nonEmptyString()` を使う。
 *
 * 本文を locale ごとの単独フィールド（`labelJa` / `labelEn` / …）に割らず 1 つの組で持つのは、
 * 言語を足すたびに本文フィールドの数が言語数ぶん増えるためである。組にしておけば、揃える
 * 言語の宣言（`SPEC_LOCALES`）1 行の変更で全フィールドが同時に追随し、追随しそこねた箇所は
 * `LocalizedText` の型エラーとして機械が見つける。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 入力スキーマの共通部品（文字列の種別・DB 参照・実装参照）と欄の値の allowlist
 * @graph-connects none
 */
import { z } from 'zod';
import { isBlankText } from './locales.js';
// =============================================================================
// 共通部品 — 全型共通の 3 属性と、複数の型から使われる値オブジェクト
// =============================================================================
/**
 * 非空文字列。言語に依らない値（`pathPattern` 等）に使う（決定 e・制約 2）。
 *
 * 人が読む本文は `localizedText()` が全 locale ぶんまとめて要求するため、この単独の文字列は
 * **翻訳の対象にならない値**にだけ残る。本文へ誤って使うと、その 1 フィールドだけが 1 言語の
 * ままグラフに入り、読む言語によって埋まっている項目が変わる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 非空必須の文字列フィールドを生成するヘルパ（制約 2 の「非空」を 1 箇所で表現する）
 * @graph-connects none
 */
export function nonEmptyString() {
    return z.string().min(1);
}
/**
 * 全 locale ぶんの本文 1 組。人が読む文字列は必ずこれで宣言する（制約 2 の多言語版）。
 *
 * どの locale も**非空必須**である。1 言語だけ書いて残りを空にした入力を受け付けると、
 * 「まだ訳していない」と「訳す必要がない」が同じ形になり、読む側にも次に書く側にも
 * 区別がつかなくなる。書込を止めることで、揃っていない本文がそもそもグラフに入らない。
 *
 * 戻り値の型注釈が `z.ZodType<LocalizedText>` なのは、`SPEC_LOCALES` に locale を足したときに
 * **この shape が追随していなければ型エラーになる**ようにするためである。宣言と shape が
 * 静かにずれる余地を消してある（制約 11 — 同じ事実の 2 表現を許さない）。
 *
 * 自動翻訳は挟まない。機械訳は業務用語と固有名詞を崩すうえ、誰も検収していない訳文が仕様
 * として残る。本文を書いたのは呼び出し側なので、各言語も呼び出し側が書く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 人が読む本文フィールドを全 locale 非空必須の組として生成するヘルパ
 * @graph-connects none
 */
export function localizedText() {
    return z.strictObject({
        ja: filledText(),
        en: filledText(),
        vi: filledText(),
    });
}
/**
 * 空白だけでない本文 1 言語ぶん。`localizedText()` の各 locale がこれを使う。
 *
 * 非空だけを見ていると `" "` が全 locale 充足を満たして通る。読取側は空欄として畳むので
 * （`isBlankText` は空白のみを空として扱う）、書けたはずの訳が画面には出ず、しかも未翻訳の
 * 印も立たない — 残作業が誰からも見えない行になる。空かどうかの判定は `isBlankText` が
 * 唯一の所有者なので、書込を止める側も同じ関数で判定する（制約 11）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 本文 1 言語が空白のみでないことを要求する（読取の空判定と同じ基準で止める）
 * @graph-connects isBlankText [calls] 空判定の唯一の所有者
 */
function filledText() {
    return z.string().refine((value) => !isBlankText(value), {
        message: '本文は空白だけにできない',
    });
}
/**
 * 人が画面から書く補足の本文（用語の備考）。1 言語で、長さの上限を置かない。
 *
 * `localizedText()` と違って 3 言語を求めない。備考は人が画面から書く欄で、AI は書けない
 * （用語の書込の MCP の入口が拒否する）。訳を書く担い手がいないので、1 言語の本文として持つ。
 * 空白だけの本文は `localizedText()` と同じ基準（`isBlankText`）で止める。
 *
 * @returns 空白だけでない 1 言語の本文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 人が画面から書く補足の本文（1 言語・上限なし・空白だけは不可）の zod 形
 * @graph-connects filledText [calls] 空白だけの本文を止める
 */
export function noteText() {
    return filledText();
}
/**
 * 変更理由の最低文字数（空白を除いた文字数）。
 *
 * **非空だけでは足りないからこの数がある。** PJ 番号は形で縛れるが、理由は自由文なので
 * 形では縛れない。非空しか見ないと「修正」「対応」「仕様変更」の一語で埋まる — 書いた本人は
 * 関門を通っており、読む人は理由が記録されている行を見て何も分からない。空欄のほうがまだ
 * 「記録されていない」と分かるぶん正直である。
 *
 * 30 に置くのは、この列が求める内容（PJ 憲章のどの狙いに対して、いま何が困っていて、だから
 * どう変えたか）を日本語で書くと最低でもこの長さになるためである。長さは中身の正しさを
 * 保証しないが、**一語で埋める経路は塞げる**。中身の質は書込ツールの description が受け持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更理由の最低文字数。一語で埋める抜け道を入力段で塞ぐ
 * @graph-connects none
 */
const SPEC_CHANGE_REASON_MIN_LENGTH = 30;
/**
 * 変更理由の形を人へ説明する 1 行。拒否メッセージとツールの description で同じ文言を使う。
 * @graph-connects none
 */
export const SPEC_CHANGE_REASON_FORMAT = `PJ 憲章とその PJ の目的に照らした変更理由（空白を除き ${String(SPEC_CHANGE_REASON_MIN_LENGTH)} 文字以上）`;
/**
 * 識別子に使える文字列。非空に加えて **制御文字・書式文字・行区切り**
 * （Unicode 一般カテゴリ `Cc` / `Cf` / `Zl` / `Zp` — 改行・復帰・タブ・NUL 等）を禁じる。
 *
 * 本文（`localizedText()`）と分けてあるのは、本文（`label` / `description`）には改行を許して
 * よい一方で、**ID として下流に流れる値は閉じた文字集合に縛る**必要があるためである。区切りに
 * なりうる制御文字が通ると 1 個の ID が下流で 2 個以上に割れ、**呼び出し元が指定していない
 * ノード / エッジまで対象に含まれうる**。「書けないものは入力できない」を表現するのが契約側の
 * 役目であり、転送方式の実装詳細に安全性をぶら下げないため、入力段で不変条件を宣言する。
 *
 * 正規表現が空文字を素通しするのは意図的で、長さの検査は `min(1)` が担う。両方が空文字を
 * 弾くと、1 つの入力ミスに対して拒否理由が 2 件返り、呼び出し側から見て直すべき点がぼやける。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ID として使う文字列フィールドを生成するヘルパ（非空 + 制御文字禁止。ID 分割による意図しない削除を入力段で止める）
 * @graph-connects none
 */
function identifierString() {
    return z
        .string()
        .min(1)
        .regex(/^[^\p{Cc}\p{Cf}\p{Zl}\p{Zp}]*$/u, 'ID に制御文字（改行・タブ等）は使えません');
}
/**
 * Term の揺れ表記 1 件（NE §1）。`text` が揺れ表記、`note` は補足。
 *
 * `text` は**多言語化しない**。揺れ表記は「同じ概念を指す別の綴り」であって訳語ではなく、
 * ある言語に何通りの揺れがあるかは言語ごとに違う。組で持つと「1 つの揺れ表記に全言語ぶんの
 * 綴りが 1 対 1 で対応する」という実在しない構造を強いることになる。言語をまたぐ揺れは
 * `synonyms[]` の**要素を足して**表す。
 *
 * `note`（その揺れが何であるかの補足）は人が読む本文なので組で持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Term の synonyms[] 要素（揺れ表記と補足）の入力スキーマ
 * @graph-connects none
 */
export const synonymSchema = z.strictObject({
    text: nonEmptyString(),
    note: localizedText().optional(),
});
/**
 * DB 識別子 1 セグメント（schema / table / column）。非空・制御文字禁止に加えて
 * **`.` を禁じる**。
 *
 * 小文字ケバブケース（`kebabIdString()`）ではないのは、DB 側の識別子が `_` を含む
 * snake_case だからである（`user_details` / `membership_status`）。spec グラフの ID 規約を
 * 外部グラフの命名へ押し付けることはできないので、ここで縛るのは**連結を壊す文字**だけにする。
 *
 * `.` を禁じるのは、2 つが `tableQualifiedName()` で `<schema>.<table>` に連結されて
 * `USES_TABLE` の `targetId` になるためである。セグメント内に `.` が入ると、同じ
 * `targetId` へ落ちる別々の組が作れてしまう。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business db-graph 側の識別子（schema / table / column）を受ける文字列フィールドを生成するヘルパ（連結の区切りである "." だけを禁じる）
 * @graph-connects none
 */
function dbIdentifierString() {
    return identifierString().regex(/^[^.]+$/u, 'DB 識別子に "." は使えない（schema / table を連結して targetId を組むため）');
}
/**
 * `usesTable` 1 件（NE §1）。db-graph の実在照合対象（制約 4）だが、実在は書込を止めない。
 *
 * 2 つは `tableQualifiedName()` で連結され `USES_TABLE` エッジの `targetId` になるため
 * 識別子として扱う。
 *
 * **段は `<schema>.<table>` の 2 段である。** db-graph 側の Table ノードは
 * `qualifiedName = <schema>.<table>` / `id = db-dictionary:table:<schema>.<table>` を持ち、
 * **`db` という段を持たない** —— DB の区別は schema 名そのものが担っている
 * （`aircloset` と `aircloset_mongo` は別 schema として並ぶ）。かつて `db` 段を先頭に足して
 * いたが、参照先の名前空間を持つ側が db-graph である以上、こちらが段を増やすと
 * **同じテーブルに 2 通りの完全修飾名ができ、どちらが正かを機械で決められない**。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Operation の usesTable[] 要素（schema / table）の入力スキーマ。db-graph の qualifiedName と同じ 2 段で書く
 * @graph-connects none
 */
export const usesTableSchema = z.strictObject({
    schema: dbIdentifierString(),
    table: dbIdentifierString(),
});
/**
 * `usesColumn` の `accessMode`。参照・書き込みの別（NE §1）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business usesColumn の accessMode（read / write）の allowlist
 * @graph-connects none
 */
export const ACCESS_MODES = ['read', 'write'];
/**
 * `usesColumn` 1 件（NE §1）。`via` は経由するテーブル参照。
 *
 * `via` / `column` は `columnQualifiedName()` で連結され `USES_COLUMN` エッジの `targetId`
 * になるため識別子として扱う。連結後は `<schema>.<table>.<column>` の 3 段になり、
 * db-graph の Column ノードの `qualifiedName` と同じ形である。
 *
 * `via` は `usesTable[]` の 2 つを連結した値、すなわち `<schema>.<table>` の
 * **ちょうど 2 セグメント**である。形式だけでは「同じ操作の `usesTable[]` に挙がっている値か」
 * までは見られないので、その突合は集約内整合（`checkOperationAggregate`）が担う。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Operation の usesColumn[] 要素（column / accessMode / via）の入力スキーマ
 * @graph-connects none
 */
export const usesColumnSchema = z.strictObject({
    column: dbIdentifierString(),
    accessMode: z.enum(ACCESS_MODES),
    via: identifierString().regex(/^[^.]+\.[^.]+$/u, 'via は usesTable[] の 1 件を連結した "<schema>.<table>" 形式（ちょうど 2 セグメント）で書く'),
});
/**
 * 接地宣言の種別。`api` は `IMPLEMENTED_BY_API`、`task` は `IMPLEMENTED_BY_TASK` になる。
 * バッチとイベントリスナーはどちらも `task`（NE §3）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business implementedBy の kind（api / task）の allowlist。IMPLEMENTED_BY_* エッジ型の振り分け元
 * @graph-connects none
 */
export const IMPLEMENTATION_KINDS = ['api', 'task'];
/**
 * 接地宣言の参照先（SPG の qualifiedName）に使える文字列。
 *
 * SPG の qualifiedName は `<所属>:<境界名>` の形を取る（`air-closet-api:POST /me/registration`・
 * `infra/pipeline/ashash/index.ts:job`）。ここで縛るのはその骨格だけ ——`:` が 1 つ以上あり、
 * 前後がともに非空で、前半に空白を含まない—— である。
 *
 * `kind` ごとに綴りを分けて縛らないのは、`task` 側の綴りの正が SPG にあって spec グラフ側には
 * 無いためである。「その名前が実在するか」は SPG 突合（制約 5）が警告として見る。ここが止めるのは
 * **所属を欠いた裸の名前**で、それは `IMPLEMENTED_BY_*` の `targetId` として突合相手を持てない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 接地宣言の ref を受ける文字列フィールドを生成するヘルパ（SPG qualifiedName の骨格 "<所属>:<境界名>" を強制する）
 * @graph-connects none
 */
function implementationRefString() {
    return identifierString().regex(/^\S+:.+$/u, 'ref は SPG の qualifiedName（"<所属>:<境界名>" 形式。例: "air-closet-api:POST /me/registration"）で書く。所属を欠いた名前は突合相手を持てない');
}
/**
 * 接地宣言（NE §1・決定 aa）。単数必須 = 原則 1。
 *
 * `ref` は SPG の qualifiedName（`air-closet-api:POST /v1/styling/consultations` 形式）で、
 * `IMPLEMENTED_BY_*` エッジの `targetId` になるため識別子として扱う。
 * 宣言は実装に先行できるため、**SPG 実在は書込を止めず警告に留める**（決定 aa・y-9）。
 *
 * 形式として縛るのは SPG の qualifiedName が `kind` によらず共有する骨格
 * ——「`:` の前に所属（リポジトリ / ファイル）、後ろに境界名」—— だけである。`kind="api"` の
 * `<repo>:<HTTP 動詞> /<path>` まで縛らないのは、`kind="task"` 側（バッチ・イベント）の
 * qualifiedName が別の綴りを取り、spec グラフ側にはその綴りの正が無いためである。
 * 綴りが正しいかどうかは SPG 突合（制約 5）が警告として見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Operation の implementedBy（kind / ref）の入力スキーマ。単数必須（原則 1・決定 aa）
 * @graph-connects none
 */
export const implementedBySchema = z.strictObject({
    kind: z.enum(IMPLEMENTATION_KINDS),
    ref: implementationRefString(),
});
/**
 * 動詞の到達状態の層（SR-06）。1 つの到達状態に 1 語だけ置く。
 *
 * - `persistent`（永続データの層）: 操作が終わった後もデータに残る（登録する・作成する …）
 * - `screen`（画面の層）: 画面の状態にだけ残り、保存はまだ（入力する・確認する・検索する）
 *
 * 「入力する」と「登録する」の使い分けは書込の有無で機械的に決まる。層と操作の帰結の一致は
 * 業務が動詞を持つようになったときにリリースゲートで見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 動詞の到達状態の層（永続データ / 画面）の allowlist
 * @graph-connects none
 */
export const VERB_LAYERS = ['persistent', 'screen'];
// =============================================================================
// UserStory 集約 — 骨格（UserStory）と詳細（Business）
// =============================================================================
/**
 * 端点の種別（開始・終了）。
 *
 * 端点は**フローがどこから始まりどこで終わるかの印**であって、業務の意味は何も持たない。
 * 持たせると、そこに書かれた行為や到達状態は業務ツリーにも画面仕様にも総合テストにも
 * 現れないまま図の上だけに存在することになり、仕様として追跡できない業務が生まれる。
 * 実際に 47 個の端点のうち 14 個がアクターの行為を述べていた。
 *
 * 何も持たせないことは型で担保する（`startFlowNodeSchema` / `endFlowNodeSchema`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business US 骨格の端点種別の allowlist（意味を持たない印の側）
 * @graph-connects none
 */
const TERMINAL_FLOW_NODE_TYPES = ['start', 'end'];
/**
 * 操作にならない制御ノードの種別（NE §1）。`process` だけが Operation を参照する。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business US 骨格の制御ノード種別の allowlist（Operation を参照しない側）
 * @graph-connects none
 */
const CONTROL_FLOW_NODE_TYPES = ['decision', ...TERMINAL_FLOW_NODE_TYPES];
/**
 * フローノードの種別。`process` だけが Operation を参照し、制御ノードは参照しない（NE §1）。
 *
 * 制御ノード側は `CONTROL_FLOW_NODE_TYPES` から展開する。両方を手で並べると、片方だけに
 * 種別を足したときに黙って乖離するためである（制約 11）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business US 骨格の nodes[] が取るフローノード種別の allowlist（process のみ Operation を参照する）
 * @graph-connects none
 */
export const FLOW_NODE_TYPES = ['process', ...CONTROL_FLOW_NODE_TYPES];
/**
 * データアクセスの種別。操作が DB を触るか触らないかを**書き手が宣言する**。
 *
 * `database` は「触る」で、`usesTable` / `usesColumn` を各 1 件以上必須にする。`none` は
 * 「触らない」で、理由を必須にする代わりに DB 参照のキー自体を書けなくする。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Operation の dataAccess（database / none）の allowlist。DB 参照の要否を分ける判別子
 * @graph-connects none
 */
export const DATA_ACCESS_KINDS = ['database', 'none'];
/**
 * 操作の種別。`normal` は業務を専門化する普通の操作、`precondition` は手前で済んだ業務を
 * 参照するだけの前提種別（仕様記述ルール SR-04）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Operation の kind（normal / precondition）の allowlist
 * @graph-connects none
 */
export const OPERATION_NODE_KINDS = ['normal', 'precondition'];
//# sourceMappingURL=node-schemas.js.map